import type { Form9Findings } from './form9-fields.js';
/**
 * Shared TypeScript types mirroring the D1 schema (PRD Section 3).
 * Used by both the worker API and the web frontend.
 */

// ─────────────────────────────────────────────
//  Companies
// ─────────────────────────────────────────────
export interface Company {
  id: string;
  short_code: string;       // e.g. 'SPPL', 'SCHOTT'
  company_name: string;     // e.g. 'M/s Schott Poonawalla Private Limited'
  occupier_name: string;    // e.g. 'Mr. Ashok S. Saxena'
  factory_address: string;
  license_number?: string;
  license_expiry_date?: string;
  registration_number?: string;
  nic_code?: string;
  contact_email?: string;
  communication_email?: string;
  contact_person?: string;
  mobile_number?: string;
  created_at: string;
  updated_at?: string;
}

// ─────────────────────────────────────────────
//  Equipment
// ─────────────────────────────────────────────
export type FirstUseType = 'EXACT_DATE' | 'YEAR_ONLY' | 'NOT_APPLICABLE' | 'NOT_KNOWN';

export interface Equipment {
  id: string;
  company_id: string;
  equipment_name_type: string;   // matches EquipmentRule.key
  equipment_short_code: string;  // e.g. 'BOPT', 'HOIST', 'CRANE'
  internal_id_number: string;    // auto: 'SCHOTT/BOPT/09'
  sequence_number: number;
  swl_value?: number;            // SWL in numeric tons
  swl_text?: string;             // text fallback
  lift_height?: string;          // e.g. '200 mm'
  make?: string;
  model?: string;
  equipment_size?: string;       // e.g. '560 mm (W) x 1150 mm (L)'
  location_in_factory?: string;
  first_use_type: FirstUseType;
  first_use_value: string;       // '2022' | '15/04/2022' | 'N/A'
  created_at: string;
  updated_at?: string;
}

// ─────────────────────────────────────────────
//  Personnel (Competent Persons & Trainers)
// ─────────────────────────────────────────────
export interface Personnel {
  id: string;
  full_name: string;
  designation: string;         // e.g. 'Competent Person', 'Head - Training Institute'
  role?: 'trainer' | 'competent_person';
  qualification?: string;
  competency_cert_number?: string;
  competency_valid_from?: string; // yyyy-mm-dd
  competency_valid_upto?: string; // yyyy-mm-dd
  issuing_authority?: string;
  signature_image_url?: string | null;  // R2 PNG URL
  stamp_image_url?: string | null;      // R2 PNG URL
  competency_cert_url?: string | null;  // R2 PDF/Image URL
  created_at: string;
  updated_at?: string;
}

// ─────────────────────────────────────────────
//  Form 10 Certificate
// ─────────────────────────────────────────────
export type DefectStatus = 'SAFE' | 'DEFECT_FOUND' | 'REJECT' | 'NOT_APPLICABLE';

export interface TestMethod {
  id: string;
  name: string;
  created_at?: string;
}

export interface Form10Certificate {
  id: string;
  certificate_number: string;   // e.g. 'G&G/SPPL/26/09'
  financial_year: string;       // e.g. '2026'
  sequence_number: number;
  company_id: string;
  equipment_id: string;
  personnel_id: string;
  examination_date: string;     // yyyy-mm-dd
  next_inspection_date: string; // yyyy-mm-dd  (exam + 1yr - 1day)
  test_method?: string;
  test_prefix_sentence: string;
  calculated_test_load: string;
  previous_inspection_details: string;
  annealing_details: string;
  defect_status: DefectStatus;
  defect_remarks: string;
  pdf_r2_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface Form10WithRelations extends Form10Certificate {
  company?: Company;
  equipment?: Equipment;
  personnel?: Personnel;
}

// ─────────────────────────────────────────────
//  Form 11 Certificate (Pressure Vessel / Plant)
// ─────────────────────────────────────────────
export interface Form11Certificate {
  id: string;
  certificate_number: string;   // e.g. 'G&G/Vidya/AR/03'
  financial_year: string;       // e.g. '2026'
  sequence_number: number;
  company_id: string;
  equipment_id?: string;
  personnel_id: string;
  examination_date: string;     // yyyy-mm-dd
  next_inspection_date: string; // yyyy-mm-dd (exam + 6 months - 1 day)

  // Vessel Identification (Point 3)
  vessel_name_description: string; // e.g. 'Vertical Air Receiver'
  vessel_serial_no?: string;
  vessel_capacity?: string;
  vessel_model_no?: string;
  vessel_location?: string;
  vessel_description?: string;

  // Manufacturer & Process (Points 4 & 5)
  manufacturer_name_address?: string;
  nature_of_process?: string;

  // Vessel Particulars (Point 6)
  date_of_construction?: string;
  thickness_shell?: string;
  thickness_dish?: string;
  thickness_top_dish?: string;
  thickness_bottom_dish?: string;
  first_taken_into_use_date?: string;
  safe_working_pressure_recommended?: string;

  // Dates of Last Examinations (Point 7)
  last_external_exam_date?: string;
  last_internal_exam_date?: string;
  last_hydraulic_exam_details?: string;
  last_ultrasonic_exam_date?: string;

  // Insulation & Lagging (Point 8)
  lagging_removed_status?: string;

  // Examination Description & Findings (Point 9)
  exam_external_findings?: string;
  exam_internal_findings?: string;
  exam_hydraulic_findings?: string;
  hydro_test_pressure?: string;
  hydro_test_due_date?: string;
  exam_ultrasonic_findings?: string;

  // Conditions of Vessel & Piping (Point 10)
  condition_vessel?: string;
  condition_piping?: string;

  // Conditions of Fittings (Point 11)
  fittings_pressure_gauges?: string;
  fittings_safety_valve?: string;
  fittings_stop_valve?: string;
  fittings_reducing_valve?: string;
  fittings_additional_safety_valve?: string;
  fittings_other_devices?: string;

  // Recommendations, Repairs & Observations (Points 12 - 16)
  safe_working_pressure_recommended_after?: string;
  repairs_required?: string;
  repairs_period?: string;
  repairs_other_condition?: string;
  reduced_working_pressure_pending_repairs?: string;
  safe_working_pressure_subrule_8?: string;
  other_observations?: string;

  pdf_r2_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface Form11WithRelations extends Form11Certificate {
  company?: Company;
  equipment?: Equipment;
  personnel?: Personnel;
}

// ─────────────────────────────────────────────
//  Training Certificate
// ─────────────────────────────────────────────
export interface TrainingCertificate {
  id: string;
  certificate_number: string;   // e.g. '460/Jun/178'
  sequence_number?: number;
  company_id: string;
  trainee_name: string;
  course_title: string;         // e.g. 'FIRE AND FIGHTING'
  conducted_by_name: string;    // faculty name 1
  faculty1_personnel_id?: string;
  faculty2_name?: string;       // faculty name 2 (optional)
  faculty2_personnel_id?: string;
  head_personnel_id?: string;
  training_date: string;        // yyyy-mm-dd
  valid_upto: string;           // training + 365 days
  validity_text?: string;       // e.g. '01 YEAR', '02 YEARS', '06 MONTHS'
  duration_days?: number;       // default 1
  approval_text: string;
  approval_no?: string;
  approval_date?: string;
  approval_valid_upto?: string;
  pdf_r2_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface TrainingCertificateWithRelations extends TrainingCertificate {
  company?: Company;
  head_personnel?: Personnel;
}

// ─────────────────────────────────────────────
//  Renewal Dashboard
// ─────────────────────────────────────────────
export type ComplianceStatus = 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE';

export interface RenewalItem {
  id: string;
  certificate_number: string;
  company_name: string;
  company_id: string;
  equipment_name: string;
  equipment_id?: string;
  internal_id_number: string;
  examination_date: string;
  next_inspection_date: string;
  status: ComplianceStatus;
  days_until_due: number;
  type: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
  communication_email?: string;
  email_sent_count?: number;
}

// ─────────────────────────────────────────────
//  API Response Shapes
// ─────────────────────────────────────────────
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface PaginationParams {
  page: number;
  limit: number;
  search?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─────────────────────────────────────────────
//  Trash & Recycle Bin (30-Day Soft Delete)
// ─────────────────────────────────────────────
export type TrashEntityType = 'form9' | 'form10' | 'form11' | 'training' | 'equipment' | 'company' | 'personnel';

export interface TrashItem {
  id: string;
  entity_type: TrashEntityType;
  entity_id: string;
  title: string;
  subtitle?: string;
  data: string;
  deleted_by?: string;
  deleted_at: string;
  days_remaining: number;
}

export interface TrashSummary {
  all: number;
  form9: number;
  form10: number;
  form11: number;
  training: number;
  equipment: number;
  company: number;
  personnel: number;
}

// ─────────────────────────────────────────────
//  User & Role-Based Access Control (RBAC)
// ─────────────────────────────────────────────
export type AppRole = 'admin' | 'read_write' | 'read_only' | 'custom';
export type PermissionLevel = 'none' | 'read' | 'write';
export type AppSection =
  | 'training'
  | 'form9'
  | 'form10'
  | 'form11'
  | 'dashboard'
  | 'renewals'
  | 'companies'
  | 'equipment'
  | 'personnel'
  | 'admin'
  | 'trash';

export type SectionPermissions = Record<AppSection, PermissionLevel>;

export interface AppUser {
  id: string;
  email: string;
  full_name?: string | null;
  role: AppRole;
  permissions: SectionPermissions;
  status: 'active' | 'disabled';
  created_at?: string;
  updated_at?: string;
}

export const ALL_APP_SECTIONS: AppSection[] = [
  'dashboard',
  'training',
  'form9',
  'form10',
  'form11',
  'renewals',
  'companies',
  'equipment',
  'personnel',
  'admin',
  'trash',
];

export const SECTION_LABELS: Record<AppSection, string> = {
  dashboard: 'Dashboard',
  training: 'Training',
  form9: 'Form 9',
  form10: 'Form 10',
  form11: 'Form 11',
  renewals: 'Renewals',
  companies: 'Companies',
  equipment: 'Equipment',
  personnel: 'Personnel',
  admin: 'Admin Management',
  trash: 'Trash',
};

/**
 * Returns default section permissions based on user role.
 */
export function getDefaultPermissions(role: AppRole): SectionPermissions {
  if (role === 'admin') {
    return {
      dashboard: 'write',
      training: 'write',
      form9: 'write',
      form10: 'write',
      form11: 'write',
      renewals: 'write',
      companies: 'write',
      equipment: 'write',
      personnel: 'write',
      admin: 'write',
      trash: 'write',
    };
  }

  if (role === 'read_write') {
    return {
      dashboard: 'read',
      training: 'write',
      form9: 'write',
      form10: 'write',
      form11: 'write',
      renewals: 'write',
      companies: 'write',
      equipment: 'write',
      personnel: 'write',
      admin: 'none',
      trash: 'write',
    };
  }

  if (role === 'read_only') {
    return {
      dashboard: 'read',
      training: 'read',
      form9: 'read',
      form10: 'read',
      form11: 'read',
      renewals: 'read',
      companies: 'read',
      equipment: 'read',
      personnel: 'read',
      admin: 'none',
      trash: 'read',
    };
  }

  // custom default
  return {
    dashboard: 'read',
    training: 'read',
    form9: 'none',
    form10: 'read',
    form11: 'read',
    renewals: 'read',
    companies: 'read',
    equipment: 'read',
    personnel: 'read',
    admin: 'none',
    trash: 'read',
  };
}

// ─────────────────────────────────────────────
//  User Drafts
// ─────────────────────────────────────────────
export type DraftEntityType = 'training' | 'form9' | 'form10' | 'form11' | 'equipment' | 'company' | 'personnel';

export interface UserDraft {
  id: string;
  user_id?: string;
  entity_type: DraftEntityType;
  title: string;
  subtitle?: string;
  form_data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface DraftSummary {
  all: number;
  training: number;
  form9: number;
  form10: number;
  form11: number;
  equipment: number;
  company: number;
  personnel: number;
}

// ─────────────────────────────────────────────
//  Company Analytics Dashboard
// ─────────────────────────────────────────────
export interface CompanyAnalyticsItem {
  company_id: string;
  company_name: string;
  short_code: string;
  factory_address?: string;
  communication_email?: string;
  contact_person?: string;
  form9_count: number;
  form10_count: number;
  form11_count: number;
  training_count: number;
  total_count: number;
  compliant_count: number;
  expiring_soon_count: number;
  overdue_count: number;
  latest_activity_date?: string;
}

export interface CompanyAnalyticsSummary {
  total_companies: number;
  total_form9: number;
  total_form10: number;
  total_form11: number;
  total_training: number;
  grand_total: number;
  total_compliant: number;
  total_expiring_soon: number;
  total_overdue: number;
}

export interface CompanyAnalyticsFilterParams {
  cert_type?: 'ALL' | 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
  financial_year?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  status?: 'ALL' | 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE';
  sort_by?: 'company_name' | 'total' | 'form9' | 'form10' | 'form11' | 'training' | 'expiring' | 'overdue';
  sort_order?: 'asc' | 'desc';
}

export interface CompanyCertificateItem {
  id: string;
  type: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
  certificate_number: string;
  company_id: string;
  company_name?: string;
  equipment_name?: string;
  equipment_id?: string;
  equipment_identifier?: string;
  trainee_name?: string;
  course_title?: string;
  issue_or_exam_date: string;
  expiry_or_due_date: string;
  status: ComplianceStatus;
  days_until_due: number;
  inspector_or_trainer?: string;
  pdf_r2_url?: string;
  created_at: string;
}

export interface CompanyCertificatesResponse {
  company: {
    id: string;
    company_name: string;
    short_code: string;
    factory_address?: string;
    communication_email?: string;
    contact_person?: string;
  };
  summary: {
    total: number;
    form9: number;
  form10: number;
    form11: number;
    training: number;
    compliant: number;
    expiring_soon: number;
    overdue: number;
  };
  certificates: CompanyCertificateItem[];
}


export interface Form9Certificate extends Form9Findings {
  id: string;
  certificate_number: string;
  financial_year: string;
  sequence_number: number;
  company_id: string;
  equipment_id: string;
  personnel_id: string;
  examination_date: string;
  next_inspection_date: string;
  pdf_r2_url?: string;
  created_at: string;
  updated_at?: string;
}
export interface Form9WithRelations extends Form9Certificate {
  company?: Company;
  equipment?: Equipment;
  personnel?: Personnel;
}
